import type {
	Choice,
	ItemFeedback,
	ItemResponse,
	OriginalItemTypes,
	ToeicArtifactType,
	UnifiedCitation,
} from "@memorang/types";
import type { ReactNode } from "react";
import { memo } from "react";

import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { isAiSubmittable } from "@memorang/helpers/session";
import type { FontSchemaType } from "../../font";
import Box from "../Box";
import { References } from "../ChatUI/References";
import { CustomListSubheader } from "../CustomListSubheader";
import { TagsList } from "../TagsList";
import AiPostAnswerHeader, { type SubSkillItem } from "./AiPostAnswerHeader";
import ExplanationContainer from "./ExplanationContainer";
import { PostAnswerCard } from "./PostAnswerCard";
import PostAnswerSheetHandle from "./PostAnswerSheetHandle";

export type PostAnswerDetailBlockProps = {
	aiAssistant: {
		icon: string;
		darkIcon?: string;
		alias: string;
		onPress?: () => void;
	};
	content: {
		explanationTitle: string;
		feedback?: ItemFeedback;
		choices: Choice[];
		tags?: string[];
		citations?: UnifiedCitation[];
		currentAiFeedback?: string;
		persistedAiFeedback?: string;
		emptyFeedbackText?: string;
	};
	scoring: {
		score?: number;
		maximumScore: number;
		overallScoreLabel: string;
		subskillAssessments: SubSkillItem[];
	};
	rendering: {
		appFont: FontSchemaType;
		fontSize: number;
		language: string;
		renderFeedbackMenu?: () => ReactNode;
		renderTtsContainer?: (explanation?: string) => ReactNode;
		streaming?: boolean;
	};
	responseType: ItemResponse["type"];
	itemType?: OriginalItemTypes;
	toeicArtifactType?: ToeicArtifactType;
	showCitations?: boolean;
	showAiHeader?: boolean;
	showExplanation?: boolean;
	showAiModalHandle?: boolean;
	forceDynamicExplanation?: boolean;
	handleExpandCollapse: () => void;
	handleNavigateToReference: (reference: UnifiedCitation) => void;
	showTextBasedCitationOnly?: boolean;
	headerIcon?: React.ReactNode;
	headerLabel: string;
};

export const PostAnswerDetailBlock = memo(
	({
		aiAssistant,
		content,
		scoring,
		rendering,
		responseType,
		itemType,
		toeicArtifactType,
		showAiHeader = true,
		showCitations,
		showExplanation,
		showAiModalHandle = true,
		forceDynamicExplanation = false,
		handleExpandCollapse,
		handleNavigateToReference,
		showTextBasedCitationOnly = false,
		headerIcon,
		headerLabel,
	}: PostAnswerDetailBlockProps) => {
		const isAiItemType = isAiSubmittable(
			responseType,
			itemType,
			toeicArtifactType,
			hasStaticFeedback(content.feedback),
		);
		const isDynamicExplanation = isAiItemType || forceDynamicExplanation;

		return (
			<Box>
				{isDynamicExplanation && showAiModalHandle ? (
					<PostAnswerSheetHandle
						handleExpandCollapse={handleExpandCollapse}
						aiAssistant={aiAssistant}
						headerIcon={headerIcon}
						headerLabel={headerLabel}
					/>
				) : null}
				<PostAnswerCard
					{...(isDynamicExplanation && showAiModalHandle
						? {
								borderTopLeftRadius: 0,
								borderTopRightRadius: 0,
								borderTopWidth: 0,
							}
						: {})}
				>
					<Box gap={16}>
						{showAiHeader && isAiItemType ? (
							<AiPostAnswerHeader
								subskillItems={scoring.subskillAssessments}
								overallScoreLabel={scoring.overallScoreLabel}
								score={scoring.score}
								totalScore={scoring.maximumScore}
								streaming={rendering.streaming}
							/>
						) : null}
						{showExplanation ? (
							<ExplanationContainer
								responseType={responseType}
								itemType={itemType}
								toeicArtifactType={toeicArtifactType}
								choices={content.choices}
								appFont={rendering.appFont}
								explanationTitle={content.explanationTitle}
								fontSize={rendering.fontSize}
								language={rendering.language}
								persistedAiFeedback={content.persistedAiFeedback}
								currentAiFeedback={content.currentAiFeedback}
								emptyFeedbackText={content.emptyFeedbackText}
								renderTtsContainer={rendering.renderTtsContainer}
								renderFeedbackMenu={rendering.renderFeedbackMenu}
								itemFeedback={content.feedback}
								streaming={rendering.streaming}
								forceDynamic={forceDynamicExplanation}
							/>
						) : null}
						{content.tags?.length ? (
							<TagsList tags={content.tags} appFont={rendering.appFont} />
						) : null}
						{showCitations && content.citations?.length ? (
							<Box>
								<CustomListSubheader
									enableGutters={false}
									textVariant="titleMedium"
									dense
								>
									References
								</CustomListSubheader>
								<References
									references={content.citations}
									showTextBasedCitationOnly={showTextBasedCitationOnly}
									withAccordion={false}
									onPressReference={handleNavigateToReference}
								/>
							</Box>
						) : null}
					</Box>
				</PostAnswerCard>
			</Box>
		);
	},
);
